import { useQueryClient } from '@tanstack/react-query';
import { CARRYFORWARD_FLOOR_ISO } from '@utazzo/shared';
import { cn, UzButton, UzDialog, UzSelect } from '@utazzo/ui';
import {
  Activity,
  CalendarCheck,
  CalendarDays,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  ClipboardList,
  CornerDownRight,
  Download,
  Layers,
  RefreshCw,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { MonthYearFilter } from '../components/MonthYearFilter';
import { LegacyTable, ReportSection, type LegacyCell } from '../components/ReportSection';
import {
  CARRY_CURRENT,
  CARRY_REACHED,
  CARRY_STATUS_FIELD,
  dmy,
  EMPTY_BUCKET,
  inr,
  isRowVisible,
  lostCount,
  ratio,
  splitCell,
  splitDrillCell,
  subCurrent,
  wonCount,
  type AgentEntry,
  type Bucketed,
} from '../components/sections/band-helpers';
import { BookingsBand } from '../components/sections/BookingsBand';
import { LeadPoolBand } from '../components/sections/LeadPoolBand';
import { LeadStatusBand } from '../components/sections/LeadStatusBand';
import { LostLeadsBand } from '../components/sections/LostLeadsBand';
import { OverviewBand } from '../components/sections/OverviewBand';
import { PerformanceBand } from '../components/sections/PerformanceBand';
import { PerformanceInsightsBand } from '../components/sections/PerformanceInsightsBand';
import { PipelineBand } from '../components/sections/PipelineBand';
import { downloadAgentTrackerExport } from '../reports.api';
import { useAgentTracker, usePerformanceDay, usePipeline, usePivot } from '../use-reports';

import type { BandProps } from '../components/sections/band-props';
import type { AgentTrackerRow, LeadListQuery, LeadPipelineRow } from '@utazzo/shared';

import { DataGrid } from '@/components/data-grid';
import { useCanExportData, useCurrentUser } from '@/features/auth/use-auth';
import { LeadsTrackerStrip } from '@/features/leads/components/LeadsTrackerStrip';
import { isoDate, istIsoDate } from '@/features/leads/lead-date-range';
import { useLeadListColumns } from '@/features/leads/lead-list-columns';
import * as leadsApi from '@/features/leads/leads.api';
import { useLeadsList } from '@/features/leads/use-leads';
import { useDataGridState } from '@/hooks/use-data-grid-state';
import { useUrlParamWriter } from '@/hooks/use-url-param-writer';

/**
 * Dialog listing the leads behind a clicked report number, rendered in the
 * EXACT same grid as the All Leads list page - same columns, same role-gating
 * (admins get the ad-attribution columns) - by reusing the real `DataGrid`
 * and the shared leads columns, fed by the leads fetched by id. Each row opens
 * `/leads/:id` in a NEW TAB so the operator keeps the report open.
 */
const LeadDrillDialog = ({
  drill,
  onClose,
}: {
  drill: { title: string; query: Partial<LeadListQuery> } | null;
  onClose: () => void;
}): React.JSX.Element => {
  const isAdmin = useCurrentUser()?.role === 'ADMIN';
  const columns = useLeadListColumns({ isAdmin });
  const gridState = useDataGridState();
  // Server-side pagination: the grid footer drives page / pageSize so the drill
  // can browse ALL matching leads, not just the first page. (pageSize is capped
  // at MAX_PAGE_SIZE=200 server-side, so a hardcoded pageSize:200 silently hid
  // everything past the first 200 - hence real paging.) useLeadsList always
  // fires; when the dialog is closed we pass a tiny 1-row query so the drill
  // fetch only runs once the operator opens it.
  const query = useLeadsList(
    drill
      ? {
          ...drill.query,
          page: gridState.state.page,
          pageSize: gridState.state.pageSize,
          sortDir: 'desc',
        }
      : { page: 1, pageSize: 1, sortDir: 'desc' },
  );
  // Reset to page 1 whenever a different number is clicked, so we never land on
  // a now-empty page from the previous (larger) drill.
  useEffect(() => {
    gridState.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drill]);
  const resolvedColumns =
    typeof columns === 'function' ? columns({ state: gridState.state }) : columns;
  // New tab, same target route the All Leads list uses (`/leads/:id`). Open
  // with a plain `_blank` (no windowFeatures string, which can spawn an
  // about:blank popup that races the SPA boot and shows "couldn't load"),
  // then sever `opener` for safety.
  const openLead = (id: string): void => {
    if (!id) return;
    const w = window.open(`/leads/${id}`, '_blank');
    if (w) w.opener = null;
  };
  return (
    <UzDialog
      open={!!drill}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={drill?.title ?? ''}
      size="full"
    >
      <DataGrid
        columns={resolvedColumns}
        data={query.data?.data ?? []}
        pagination={
          query.data
            ? {
                page: query.data.pagination.page,
                pageSize: query.data.pagination.pageSize,
                totalItems: query.data.pagination.totalItems,
              }
            : null
        }
        hideToolbar
        state={gridState.state}
        onStateChange={{
          setSearch: gridState.setSearch,
          setFilter: gridState.setFilter,
          setSort: gridState.setSort,
          setPage: gridState.setPage,
          setPageSize: gridState.setPageSize,
          setDensity: gridState.setDensity,
          setHidden: gridState.setHidden,
        }}
        isLoading={query.isLoading}
        isError={query.isError}
        getRowId={(row) => row.id}
        tableLayout="auto"
        onRowClick={(row) => openLead(row.id)}
        emptyTitle="No leads."
      />
    </UzDialog>
  );
};

/**
 * Port of the legacy `/qms/dashboard` "Daily Leads Tracker" page on the new
 * reports API, organised into top tabs (Overview / Pipeline / Performance /
 * Lead Status / Lost Leads / Bookings & Destinations). Each tab shows its own
 * KPI cards above the related tables; tab-specific queries only fire once the
 * tab is opened.
 *
 * `embedded` renders the report without its page header + own DateRangeFilter,
 * for hosting inside the Leads inbox (below Targets). The date range then comes
 * from the inbox's own DateRangeFilter, which writes the same URL params this
 * report already reads. Export + refresh live in the report's own toolbar
 * (above the tabs) in both modes, so they sit inside the report section rather
 * than the page top.
 */
export const DailyTrackerPage = ({
  embedded = false,
}: { embedded?: boolean } = {}): React.JSX.Element => {
  const [params] = useSearchParams();
  // Writes merge into the LIVE URL: this page also runs a nuqs-backed DataGrid
  // (the drill-in list), whose keys React Router's snapshot never sees.
  const writeParams = useUrlParamWriter();
  // Reporting period is a single MONTH + YEAR (default: current month). Carry-
  // forward is a month-boundary concept, so a fixed month start/end keeps every
  // metric stable and reproducible (a rolling date range floats the boundary).
  const nowRef = new Date();
  const selMonth = Number(params.get('month')) || nowRef.getMonth() + 1; // 1-12
  const selYear = Number(params.get('year')) || nowRef.getFullYear();
  const isCurrentMonth = selMonth === nowRef.getMonth() + 1 && selYear === nowRef.getFullYear();
  const setMonthYear = (month: number, year: number): void => {
    writeParams((p) => {
      p.set('month', String(month));
      p.set('year', String(year));
    });
  };

  const today = isoDate(nowRef)!;
  // Current month -> month-to-date (end = today); a past month -> the full,
  // frozen month (end = its last day). Carry-forward keys off `from` (the 1st).
  const from = isoDate(new Date(selYear, selMonth - 1, 1))!;
  const to = isCurrentMonth ? today : isoDate(new Date(selYear, selMonth, 0))!;

  const tracker = useAgentTracker(from, to);
  const todayTracker = useAgentTracker(today, today);
  // Every report section is now stacked (no tabs), so each query loads up front
  // instead of only when its tab is opened.
  const todaysPipeline = usePipeline(from, to, 'today');
  const expectedPipeline = usePipeline(from, to, 'expected');
  const [lostFilter, setLostFilter] = useState<string>('all');
  const lostReasonPivot = usePivot(
    from,
    to,
    'lost_reason',
    false,
    lostFilter === 'all' ? undefined : lostFilter,
  );
  const destinationPivot = usePivot(from, to, 'destination');

  // Performance Insights day picker: a single IST day (default = today; past
  // days selectable, no future). On TODAY the section keeps the existing live
  // "today" snapshot table (built off `report`); on a PAST day it swaps to the
  // timestamp-reconstructed follow-up-productivity view from its own endpoint.
  const istToday = istIsoDate();
  const perfDay = params.get('perfDay') || istToday;
  const isPerfToday = perfDay >= istToday; // clamp: never a future day
  const setPerfDay = (d: string): void => {
    writeParams((p) => {
      // Storing today (or empty) drops the param so the URL stays clean and the
      // section falls back to the live snapshot.
      if (d && d < istToday) p.set('perfDay', d);
      else p.delete('perfDay');
    });
  };
  const perfDayQuery = usePerformanceDay(!isPerfToday ? perfDay : null);

  const report = tracker.data;
  const loading = tracker.isLoading;
  const totals: AgentTrackerRow | undefined = report?.totals;
  const me = useCurrentUser();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Lost Trustability (approval-acceptance %) is a management metric - only
  // ADMIN and SALES_LEAD see it; SALES_AGENT does not.
  const canSeeTrustability = me?.role === 'ADMIN' || me?.role === 'SALES_LEAD';

  // Owner filter for the OVERVIEW KPIs ONLY (the Business Number table keeps the
  // full per-agent breakdown - it has its own drill-down). null = All (uses the
  // aggregate totals row). Options are the role-scoped agents the tracker already
  // returned (admin -> everyone, sales-lead -> their team), so it mirrors the
  // Lead Inbox owner dropdown's visibility without any extra query.
  // Embedded under the Lead Inbox, the owner filter IS the inbox's shared
  // `?ownerId=` param (same mechanism as the date) - so changing the inbox owner
  // updates these KPIs too. Standalone uses the report's own header dropdown.
  const [localOwnerId, setLocalOwnerId] = useState<string | null>(null);
  const ownerId = embedded ? params.get('ownerId') || null : localOwnerId;
  const setOwnerId = setLocalOwnerId;
  const ownerOptions = useMemo(
    () =>
      (report?.rows ?? [])
        .filter((r) => r.agentId)
        .map((r) => ({ id: r.agentId as string, name: r.agentName }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [report],
  );
  // Standalone only: reset to All if the picked owner drops out of scope (e.g.
  // after a month change). Embedded owner comes from the inbox URL - never touch it.
  useEffect(() => {
    if (!embedded && localOwnerId && report && !report.rows.some((r) => r.agentId === localOwnerId))
      setLocalOwnerId(null);
  }, [embedded, localOwnerId, report]);
  // The aggregate the KPIs read: a single agent's row, or the whole-pool totals.
  const scopedRow: AgentTrackerRow | undefined = ownerId
    ? (report?.rows.find((r) => r.agentId === ownerId) ?? totals)
    : totals;

  // ----- Master (SALES_LEAD) → child (assigned agents) grouping -----
  const [expandedLeads, setExpandedLeads] = useState<Set<string>>(new Set());
  // Realised Pipeline: second-level expansion of a leaf agent -> per-day rows.
  const [expandedRealisedDays, setExpandedRealisedDays] = useState<Set<string>>(new Set());
  // Lead drill-down dialog (opened by clicking a lead-referencing number).
  const [drill, setDrill] = useState<{ title: string; query: Partial<LeadListQuery> } | null>(null);
  const toggleRealisedDays = (agentId: string): void =>
    setExpandedRealisedDays((prev) => {
      const next = new Set(prev);
      if (next.has(agentId)) next.delete(agentId);
      else next.add(agentId);
      return next;
    });
  const agentEntries = useMemo((): AgentEntry[] => {
    const rows = report?.rows ?? [];
    const leadIds = new Set(
      rows
        .filter((row) => row.role === 'SALES_LEAD' && row.agentId)
        .map((row) => row.agentId as string),
    );
    const childrenByLead = new Map<string, AgentTrackerRow[]>();
    for (const row of rows) {
      if (
        row.reportingManagerId &&
        leadIds.has(row.reportingManagerId) &&
        row.agentId !== row.reportingManagerId
      ) {
        // Hide team members with no data; a deactivated member also needs real
        // period activity (not just stale open leads) to show - see isRowVisible.
        if (!isRowVisible(row)) continue;
        const siblings = childrenByLead.get(row.reportingManagerId) ?? [];
        siblings.push(row);
        childrenByLead.set(row.reportingManagerId, siblings);
      }
    }
    const entries: AgentEntry[] = [];
    for (const row of rows) {
      // Child rows render under their lead, not at the top level.
      if (
        row.reportingManagerId &&
        leadIds.has(row.reportingManagerId) &&
        row.agentId !== row.reportingManagerId
      ) {
        continue;
      }
      const children = row.agentId ? (childrenByLead.get(row.agentId) ?? []) : [];
      // Hide a top-level row with no data AND no visible children; a lead with a
      // visible team stays (to parent them) even if they have no personal data.
      if (!isRowVisible(row) && children.length === 0) continue;
      entries.push({ row, depth: 0, childCount: children.length });
      if (row.agentId && expandedLeads.has(row.agentId)) {
        for (const child of children) entries.push({ row: child, depth: 1, childCount: 0 });
      }
    }
    return entries;
  }, [report, expandedLeads]);

  const toggleLead = (agentId: string): void => {
    setExpandedLeads((prev) => {
      const next = new Set(prev);
      if (next.has(agentId)) {
        next.delete(agentId);
      } else {
        next.add(agentId);
      }
      return next;
    });
  };

  // Every master (SALES_LEAD) row that actually has children under it - the
  // universe the expand-all / collapse-all toggle acts on.
  const expandableLeadIds = useMemo(
    () =>
      agentEntries
        .filter((entry) => entry.depth === 0 && entry.childCount > 0 && entry.row.agentId)
        .map((entry) => entry.row.agentId as string),
    [agentEntries],
  );
  const allExpanded =
    expandableLeadIds.length > 0 && expandableLeadIds.every((id) => expandedLeads.has(id));
  const toggleAll = (): void =>
    setExpandedLeads(allExpanded ? new Set() : new Set(expandableLeadIds));

  // Every reporting table lands fully expanded for EVERYONE (once, on first
  // load, across every tab); the operator can still collapse rows manually
  // afterwards via the per-row chevron or the Collapse-all control.
  const didAutoExpand = useRef(false);
  useEffect(() => {
    if (!didAutoExpand.current && expandableLeadIds.length > 0) {
      setExpandedLeads(new Set(expandableLeadIds));
      didAutoExpand.current = true;
    }
  }, [expandableLeadIds]);

  // Shared expand/collapse-all control, dropped into every hierarchical table's
  // header. Hidden when there are no expandable (team) rows.
  const expandAllAction =
    expandableLeadIds.length > 0 ? (
      <button
        type="button"
        onClick={toggleAll}
        className="border-border text-muted-foreground hover:bg-muted hover:text-foreground flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors"
      >
        {allExpanded ? (
          <ChevronsDownUp className="h-3.5 w-3.5" />
        ) : (
          <ChevronsUpDown className="h-3.5 w-3.5" />
        )}
        {allExpanded ? 'Collapse all' : 'Expand all'}
      </button>
    ) : undefined;

  /** First cell of every agent table: expandable lead / indented child / plain agent. */
  const agentCell = (entry: AgentEntry): React.ReactNode => {
    const { row, depth, childCount } = entry;
    if (childCount > 0 && row.agentId) {
      const open = expandedLeads.has(row.agentId);
      return (
        <button
          type="button"
          onClick={() => toggleLead(row.agentId as string)}
          title={open ? 'Hide team agents' : 'Show team agents'}
          className="text-foreground flex items-center gap-1.5 font-semibold"
        >
          <ChevronRight
            className={cn(
              'text-muted-foreground h-3.5 w-3.5 shrink-0 transition-transform',
              open && 'rotate-90',
            )}
          />
          {row.agentName}
          <span className="bg-primary/10 text-primary rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums">
            {childCount}
          </span>
        </button>
      );
    }
    if (depth === 1) {
      return (
        <span className="text-muted-foreground flex items-center gap-1.5 pl-4">
          <CornerDownRight className="h-3.5 w-3.5 shrink-0" />
          {row.agentName}
        </span>
      );
    }
    return row.agentName;
  };

  // Every calendar day of the selected month (1st -> last day), so an expanded
  // person shows the WHOLE month day-by-day, zeros included.
  const monthDays = useMemo((): string[] => {
    const days: string[] = [];
    const end = isoDate(new Date(selYear, selMonth, 0))!; // day 0 of next month = last day
    for (
      let cursor = new Date(`${from}T00:00:00`);
      isoDate(cursor)! <= end;
      cursor.setDate(cursor.getDate() + 1)
    ) {
      days.push(isoDate(cursor)!);
    }
    return days;
  }, [from, selYear, selMonth]);

  // ----- Realised Pipeline: per-agent (role hierarchy) + per-day drill -----
  // Aggregate the MTD 'expected' pipeline by agent, and by day within agent.
  // Realised = WON and closed on exactly the expected payment date ("on the dot"),
  // matching buildRealisedRows. Realised TTV sums ttvAmount (turnover basis).
  const realisedByAgent = useMemo(() => {
    const map = new Map<string, Bucketed & { days: Map<string, Bucketed> }>();
    for (const r of expectedPipeline.data?.rows ?? []) {
      if (!r.assignedAgentId) continue;
      const day = r.expectedPaymentDate;
      const isRealised = r.closedAt?.slice(0, 10) === day && r.bucket === 'WON';
      let a = map.get(r.assignedAgentId);
      if (!a) {
        a = { files: [], realised: [], days: new Map() };
        map.set(r.assignedAgentId, a);
      }
      a.files.push(r);
      if (isRealised) a.realised.push(r);
      let d = a.days.get(day);
      if (!d) {
        d = { files: [], realised: [] };
        a.days.set(day, d);
      }
      d.files.push(r);
      if (isRealised) d.realised.push(r);
    }
    return map;
  }, [expectedPipeline.data]);

  // A clicked number opens the leads behind it (by explicit ids OR by a filter
  // query) in the drill dialog. `count` decides whether it's clickable at all.
  const drillCell = (
    display: React.ReactNode,
    sortVal: number,
    count: number,
    title: string,
    query: Partial<LeadListQuery>,
  ): LegacyCell => {
    if (count === 0) return { node: display, sort: sortVal };
    return {
      node: (
        <button
          type="button"
          onClick={() => setDrill({ title, query })}
          className="text-primary cursor-pointer font-medium underline-offset-2 hover:underline"
        >
          {display}
        </button>
      ),
      sort: sortVal,
    };
  };
  // Realised-pipeline numbers drill by an explicit id list (the exact subset).
  const leadCell = (
    display: React.ReactNode,
    sortVal: number,
    title: string,
    leads: LeadPipelineRow[],
  ): LegacyCell =>
    drillCell(display, sortVal, leads.length, title, {
      ids: leads.map((r) => r.leadId).join(','),
    });
  // The 5 metric cells for one bucket - Files / Realised / % / TTV / Realised TTV,
  // with every lead-referencing number clickable (% is not a lead reference).
  const bucketCells = (prefix: string, b: Bucketed): LegacyCell[] => {
    const ttv = b.files.reduce((s, r) => s + (r.ttvAmount ?? 0), 0);
    // 2026-08-05. Realised TTV now sums ttvAmount (turnover), matching the TTV
    // column + the turnover-everywhere reporting basis (was saleAmount).
    const realisedTtv = b.realised.reduce((s, r) => s + (r.ttvAmount ?? 0), 0);
    return [
      leadCell(b.files.length, b.files.length, `${prefix} - pipeline files`, b.files),
      leadCell(b.realised.length, b.realised.length, `${prefix} - realised`, b.realised),
      ratio(b.realised.length, b.files.length),
      leadCell(inr(ttv), ttv, `${prefix} - pipeline files`, b.files),
      leadCell(inr(realisedTtv), realisedTtv, `${prefix} - realised`, b.realised),
    ];
  };

  // Flattened rows: reuse the SAME role hierarchy as Business Number
  // (agentEntries). Every person can also drill to their full-month day-by-day.
  const realisedRows = useMemo((): LegacyCell[][] => {
    const out: LegacyCell[][] = [];
    for (const entry of agentEntries) {
      const agentId = entry.row.agentId;
      const agg = agentId ? realisedByAgent.get(agentId) : undefined;
      const open = !!agentId && expandedRealisedDays.has(agentId);
      const first: LegacyCell = {
        node: (
          <span className="flex items-center gap-2">
            {agentCell(entry)}
            {agentId && (
              <button
                type="button"
                onClick={() => toggleRealisedDays(agentId)}
                title={open ? 'Hide day-by-day' : 'Show day-by-day'}
                className={cn(
                  'shrink-0 rounded p-0.5 transition-colors',
                  open
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                <CalendarDays className="h-3.5 w-3.5" />
              </button>
            )}
          </span>
        ),
        sort: entry.row.agentName,
      };
      out.push([first, ...bucketCells(entry.row.agentName, agg ?? EMPTY_BUCKET)]);
      if (open) {
        // Full month, 1st -> last day, zeros where the person had no files.
        for (const day of monthDays) {
          const d = agg?.days.get(day) ?? EMPTY_BUCKET;
          out.push([
            { node: <span className="text-muted-foreground pl-10">{dmy(day)}</span>, sort: day },
            ...bucketCells(`${entry.row.agentName} - ${dmy(day)}`, d),
          ]);
        }
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentEntries, realisedByAgent, expandedRealisedDays, monthDays]);

  // Grand total across all agents (assigned-only, matching the pipeline query).
  const realisedTotalRow = useMemo((): LegacyCell[] | undefined => {
    if (!expectedPipeline.data) return undefined;
    const files: LeadPipelineRow[] = [];
    const realised: LeadPipelineRow[] = [];
    for (const a of realisedByAgent.values()) {
      files.push(...a.files);
      realised.push(...a.realised);
    }
    return ['Total', ...bucketCells('Total', { files, realised })];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realisedByAgent, expectedPipeline.data]);

  const canExport = useCanExportData();
  const [exporting, setExporting] = useState(false);
  const onExport = async (): Promise<void> => {
    setExporting(true);
    try {
      await downloadAgentTrackerExport(from, to);
    } catch {
      toast.error('Could not export the report.');
    } finally {
      setExporting(false);
    }
  };

  // ----- Auto-refresh (queries poll every 60s) + manual refresh & "updated ago" -----
  const queryClient = useQueryClient();
  const refreshing =
    tracker.isFetching ||
    todayTracker.isFetching ||
    todaysPipeline.isFetching ||
    expectedPipeline.isFetching ||
    lostReasonPivot.isFetching ||
    destinationPivot.isFetching;
  const onRefresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['reports'] });
  };
  // Re-render every 30s so the relative "updated ago" label stays current.
  const [, setClockTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setClockTick((tick) => tick + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);
  const updatedAgo = ((): string | null => {
    if (!tracker.dataUpdatedAt) return null;
    const mins = Math.floor((Date.now() - tracker.dataUpdatedAt) / 60_000);
    if (mins <= 0) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
  })();

  // ----- Derived KPI numbers -----
  // Owner-scoped versions of the tab KPIs (from scopedRow).
  const scopedWon = scopedRow && report ? wonCount(report, scopedRow) : undefined;
  const scopedActive = scopedRow
    ? (report?.subStatuses ?? []).reduce(
        (sum, sub) => sum + (scopedRow.bySubStatus[sub.code] ?? 0),
        0,
      )
    : undefined;
  // Combined "lost this period" = month-created leads now lost + carried-in
  // leads now lost (carriedIn.currentLost) - same pattern as Converted (Won),
  // and equal to the Lost Reasons pivot grand total.
  const scopedLost =
    scopedRow && report
      ? lostCount(report, scopedRow) + scopedRow.carriedIn.currentLost
      : undefined;
  const scopedLostCurrent = scopedRow && report ? lostCount(report, scopedRow) : undefined;
  // Avg Ticket Value = won TTV / bookings (won count) - the average deal size.
  const scopedAtv =
    scopedRow && scopedWon && scopedWon > 0 ? scopedRow.wonTtvAmount / scopedWon : null;

  // Pipeline KPIs scope by filtering the flat lead list on the picked owner
  // (the pipeline TABLES still render every row). null owner = whole pool.
  const scopePipeRows = (rows: LeadPipelineRow[]): LeadPipelineRow[] =>
    ownerId ? rows.filter((r) => r.assignedAgentId === ownerId) : rows;
  const sumPipeTtv = (rows: LeadPipelineRow[]): number =>
    rows.reduce((sum, r) => sum + (r.ttvAmount ?? 0), 0);
  const todayPipeRows = scopePipeRows(todaysPipeline.data?.rows ?? []);
  const mtdPipeRows = scopePipeRows(expectedPipeline.data?.rows ?? []);
  const realisedCount = mtdPipeRows.filter(
    (row) => row.closedAt?.slice(0, 10) === row.expectedPaymentDate && row.bucket === 'WON',
  ).length;

  // Shimmer placeholder shown in KPI cards while their query loads.
  const dash = (
    <span className="bg-muted inline-block h-6 w-14 animate-pulse rounded align-middle" />
  );

  // Always "October 2026"-style for the redesign section mini-labels, which
  // carry their own "+ carry forward" suffix.
  const periodLabel = `${new Date(selYear, selMonth - 1, 1).toLocaleString('en-US', { month: 'long' })} ${selYear}`;
  // The standalone "Unassigned" tile counts only leads that still need an owner
  // (OPEN bucket) - a Lost/Won lead is closed and never needs assigning. The
  // pool-split above keeps the full counts.
  const unassignedOpenCurrent = ownerId ? 0 : (report?.unassignedOpenLeads ?? 0);
  const unassignedOpenCarry = ownerId ? 0 : (report?.unassignedOpenCarriedIn ?? 0);

  // ----- Clickable drill-ins -----
  // Each report tile opens the two-pane Lead detail with a filter that
  // reproduces the tile's cohort, so the aside list Total equals the tile. We
  // fetch the first matching lead id (cheap pageSize:1), then navigate to it
  // with URL params the aside rebuilds the SAME query from.
  const statusIdByCode = (code: string): string | undefined =>
    report?.statuses.find((s) => s.code === code)?.id;
  const subStatusIdByCode = (code: string): string | undefined =>
    report?.subStatuses.find((s) => s.code === code)?.id;
  const openReportTile = (
    tileKey: string,
    query: Partial<LeadListQuery>,
    urlParams: Record<string, string>,
  ): void => {
    void (async () => {
      const full = {
        page: 1,
        pageSize: 1,
        sortDir: 'desc',
        excludeWebsite: true,
        ...(ownerId ? { assignedAgentId: ownerId } : { includeUnassignedPool: true }),
        ...query,
      } as LeadListQuery;
      const suffix = new URLSearchParams(urlParams);
      if (ownerId) suffix.set('ownerId', ownerId);
      try {
        const res = await qc.fetchQuery({
          queryKey: ['leads', 'report-tile', tileKey, full],
          queryFn: () => leadsApi.listLeads(full),
          staleTime: 5_000,
        });
        const id = res.data[0]?.id;
        navigate(id ? `/leads/${id}?${suffix}` : `/leads?tab=pipeline&${suffix}`);
      } catch {
        navigate(`/leads?tab=pipeline&${suffix}`);
      }
    })();
  };
  // Carry fold shared by every "Total" (current month + carry) drill: pull in
  // the still-open carried backlog floored at the cutover, matching the report.
  const CARRY_FOLD = {
    includeActiveCarryover: true as const,
    activeCarryoverFrom: CARRYFORWARD_FLOOR_ISO,
  };
  // The window travels on `rptFrom`/`rptTo` (the aside strips `from`/`to`).
  // `rptCarry` folds the still-open carried backlog; `rptClosed` folds this-
  // period closures (Total pool). These apply to the chip badges too, so the
  // chips reflect the same cohort as the drilled list.
  const totWindow = { rptExplicit: '1', rptFrom: from, rptTo: to };
  const openTotalPool = (): void =>
    openReportTile(
      'total:pool',
      { createdAtFrom: from, createdAtTo: to, ...CARRY_FOLD, includeClosedThisPeriod: true },
      { ...totWindow, rptCarry: '1', rptClosed: '1' },
    );
  const openTotalStage = (code: string): void => {
    const id = statusIdByCode(code);
    if (!id) return;
    openReportTile(
      `total:stage:${code}`,
      {
        createdAtFrom: from,
        createdAtTo: to,
        currentStatusId: id,
        currentBucket: 'OPEN',
        ...CARRY_FOLD,
      },
      { ...totWindow, rptCarry: '1', stage: id },
    );
  };
  const openTotalSub = (code: string): void => {
    const id = subStatusIdByCode(code);
    const activeId = statusIdByCode('active');
    if (!id || !activeId) return;
    openReportTile(
      `total:sub:${code}`,
      {
        createdAtFrom: from,
        createdAtTo: to,
        currentSubStatusId: id,
        currentStatusId: activeId,
        currentBucket: 'OPEN',
        ...CARRY_FOLD,
      },
      { ...totWindow, rptCarry: '1', substage: id },
    );
  };
  // Won/Lost reroute to closedAt for terminal buckets (ADR-0058), so the drill
  // shows "closed in the selected month". For the CURRENT month this equals the
  // tile (current-status won/lost of the pool); for a PAST month it can differ
  // (the tile counts where leads are now). Documented as a known caveat.
  const openTotalTerminal = (bucket: 'WON' | 'LOST'): void =>
    openReportTile(
      `total:${bucket}`,
      { createdAtFrom: from, createdAtTo: to, currentBucket: bucket },
      { ...totWindow, bucket },
    );
  const openTotalLarge = (): void =>
    openReportTile(
      'total:large',
      {
        createdAtFrom: from,
        createdAtTo: to,
        largeLeadQuery: true,
        currentBucket: 'OPEN',
        ...CARRY_FOLD,
      },
      { ...totWindow, rptCarry: '1', largeLeadQuery: 'true' },
    );

  // ---- Current section drills: created in the selected month ONLY (no carry).
  // `rptExplicit=1` + `rptFrom`/`rptTo` make the aside use the literal window,
  // bypassing its always-on combined pool. Exact for any month.
  const curWindow = { rptExplicit: '1', rptFrom: from, rptTo: to };
  const openCurrentPool = (): void =>
    openReportTile('current:pool', { createdAtFrom: from, createdAtTo: to }, curWindow);
  const openCurrentStage = (code: string): void => {
    const id = statusIdByCode(code);
    if (!id) return;
    openReportTile(
      `current:stage:${code}`,
      { createdAtFrom: from, createdAtTo: to, currentStatusId: id, currentBucket: 'OPEN' },
      { ...curWindow, stage: id },
    );
  };
  const openCurrentSub = (code: string): void => {
    const id = subStatusIdByCode(code);
    const activeId = statusIdByCode('active');
    if (!id || !activeId) return;
    openReportTile(
      `current:sub:${code}`,
      {
        createdAtFrom: from,
        createdAtTo: to,
        currentSubStatusId: id,
        currentStatusId: activeId,
        currentBucket: 'OPEN',
      },
      { ...curWindow, substage: id },
    );
  };
  const openCurrentLarge = (): void =>
    openReportTile(
      'current:large',
      { createdAtFrom: from, createdAtTo: to, largeLeadQuery: true, currentBucket: 'OPEN' },
      { ...curWindow, largeLeadQuery: 'true' },
    );

  // ---- Carry section drills: created before the month, OPEN as of the 1st.
  // `openAsOf=<monthStart>` reproduces `fetchCarriedInCounts` exactly (incl.
  // carry leads that closed later in the month). Exact for any month.
  const carryFloor = CARRYFORWARD_FLOOR_ISO;
  const prevMonthEnd = isoDate(new Date(selYear, selMonth - 1, 0))!;
  const monthStart = from; // YYYY-MM-01 of the selected month
  const carryWindow = {
    rptExplicit: '1',
    rptFrom: carryFloor,
    rptTo: prevMonthEnd,
    openAsOf: monthStart,
  };
  const carryQueryBase = {
    createdAtFrom: carryFloor,
    createdAtTo: prevMonthEnd,
    openAsOf: monthStart,
  } as const;
  const openCarryPool = (): void =>
    openReportTile('carry:pool', { ...carryQueryBase }, carryWindow);
  const openCarryStage = (code: string): void => {
    const id = statusIdByCode(code);
    if (!id) return;
    openReportTile(
      `carry:stage:${code}`,
      { ...carryQueryBase, currentStatusId: id, currentBucket: 'OPEN' },
      { ...carryWindow, stage: id },
    );
  };
  const openCarrySub = (code: string): void => {
    const id = subStatusIdByCode(code);
    const activeId = statusIdByCode('active');
    if (!id || !activeId) return;
    openReportTile(
      `carry:sub:${code}`,
      {
        ...carryQueryBase,
        currentSubStatusId: id,
        currentStatusId: activeId,
        currentBucket: 'OPEN',
      },
      { ...carryWindow, substage: id },
    );
  };
  const openCarryLarge = (): void =>
    openReportTile(
      'carry:large',
      { ...carryQueryBase, largeLeadQuery: true, currentBucket: 'OPEN' },
      { ...carryWindow, largeLeadQuery: 'true' },
    );

  // "Active w/ Itinerary" tile -> the active leads NOT yet published: in-progress
  // (a draft itinerary) + none (no itinerary at all). Same cohort as the coverage
  // metric (active, OPEN, created on/after floor), i.e. the whole In-progress+None gap.
  const openActiveUnpublished = (): void => {
    const activeId = statusIdByCode('active');
    if (!activeId) return;
    openReportTile(
      'active:unpublished',
      {
        createdAtFrom: CARRYFORWARD_FLOOR_ISO,
        createdAtTo: to,
        currentStatusId: activeId,
        currentBucket: 'OPEN',
        itineraryState: 'unpublished',
        // Includes unassigned (the coverage denominator matches the Active tile).
      },
      {
        rptExplicit: '1',
        rptFrom: CARRYFORWARD_FLOOR_ISO,
        rptTo: to,
        stage: activeId,
        itineraryState: 'unpublished',
      },
    );
  };

  // The Total / Current / Carry pool sections, rendered ABOVE the report tab
  // bar (just below the inbox Daily Tracker) rather than inside the Overview
  // tab, so the headline pool numbers are always visible.
  // ----- Lead Inbox redesign (Claude Design v1) numeric accessors -----
  // Every card below shows: Total (month + carry) + a share-of-pool % + a
  // stacked bar (this-month solid / carry faded) + a "This month / Carry" foot.
  // All values map onto the SAME fields the retired Total/Current/Carry sections
  // used, so the whole-card drill is the "Total" cohort and the foot numbers
  // drill the Current / Carry cohorts (every open* handler stays wired).
  const poolTotal = scopedRow ? scopedRow.totalLeads + scopedRow.carriedInLeads : 0;
  const poolMonth = scopedRow ? scopedRow.totalLeads : 0;
  const poolCarry = scopedRow ? scopedRow.carriedInLeads : 0;
  const poolUnassigned = unassignedOpenCurrent + unassignedOpenCarry;
  const stMonth = (code: string): number => scopedRow?.byStatus[code] ?? 0;
  const stCarry = (code: string): number => {
    const k = CARRY_STATUS_FIELD[code];
    return scopedRow && k ? scopedRow.carriedIn[k] : 0;
  };
  const stTotal = (code: string): number => stMonth(code) + stCarry(code);
  const sbMonth = (code: string): number => scopedRow?.bySubStatus[code] ?? 0;
  const sbCarryCur = (code: string): number => {
    const k = CARRY_CURRENT[code];
    return scopedRow && k ? scopedRow.carriedIn[k] : 0;
  };
  const sbTotal = (code: string): number => (scopedRow ? subCurrent(scopedRow, code) : 0);
  const sbMtd = (code: string): number => scopedRow?.reachedBySubStatus[code] ?? 0;
  const sbCarryCum = (code: string): number => {
    const k = CARRY_REACHED[code];
    return scopedRow && k ? scopedRow.carriedIn[k] : 0;
  };
  const activeSum = sbTotal('proposal') + sbTotal('warm') + sbTotal('hot');
  const largeMonth = scopedRow ? scopedRow.largeLeadStats.openCurrent : 0;
  const largeCarry = scopedRow ? scopedRow.largeLeadStats.openCarryFwd : 0;
  const largeTotal = largeMonth + largeCarry;
  const lstats = scopedRow?.largeLeadStats;
  const perfWon = scopedWon ?? 0;
  const lossDecided = scopedRow ? scopedRow.lostApproved + scopedRow.lostRejected : 0;

  // One "Agent by Daily Tasks (Today only)" row. Today's Fresh is a plain
  // today-created count (no Curr/CF split) and drills to leads created today;
  // Today's Follow Up / Missed Follow Up / Revert Pending are Curr/CF splits
  // drilling the combined pool floored at the carry cutover (matching the
  // split's total), mirroring the operational Daily Tracker strip legs. These
  // are LIVE/today-based (not scoped to the report period). Total Tasks has no
  // single lead-list filter (it is a distinct union), so it stays a plain,
  // non-clickable number.
  const istYesterday = istIsoDate(new Date(Date.now() - 24 * 60 * 60 * 1000));
  const dailyTasksRow = (r: AgentTrackerRow): LegacyCell[] => {
    const b = r.dailyTasksBreakdown;
    const id = (r.agentId as string | null) ?? null;
    // The backend Today FU / Missed FU counts exclude Not Qualified leads, so the
    // drill must exclude them too or the listed leads exceed the cell number.
    const nqId = statusIdByCode('not_qualified');
    const excludeNq = nqId ? { excludeStatusId: nqId } : {};
    const totalCur = b.freshToday + b.todayFuCur + b.missedFuCur + b.revertCur;
    const totalCf = b.todayFuCf + b.missedFuCf + b.revertCf;
    return [
      drillCell(b.freshToday, b.freshToday, b.freshToday, `${r.agentName} - Today's Fresh`, {
        ...(id ? { assignedAgentId: id } : {}),
        createdAtFrom: istToday,
        createdAtTo: istToday,
      }),
      splitDrillCell(
        drillCell,
        b.todayFuCur,
        b.todayFuCf,
        id,
        `${r.agentName} - Today's Follow Up`,
        {
          currentBucket: 'OPEN',
          nextFollowUpFrom: istToday,
          nextFollowUpTo: istToday,
          ...excludeNq,
        },
      ),
      splitDrillCell(
        drillCell,
        b.missedFuCur,
        b.missedFuCf,
        id,
        `${r.agentName} - Missed Follow Up`,
        {
          currentBucket: 'OPEN',
          nextFollowUpTo: istYesterday,
          ...excludeNq,
        },
      ),
      splitDrillCell(drillCell, b.revertCur, b.revertCf, id, `${r.agentName} - Revert Pending`, {
        revertPending: true,
        currentBucket: 'OPEN',
      }),
      splitCell(totalCur, totalCf),
    ];
  };

  // The single prop contract every band (section component) receives. Bands are
  // presentational: all hooks, data fetching and state live here; this passes
  // the already-computed values, the shared table closures and the drill
  // handlers down. The render order below follows the client dashboard flow;
  // split bands (Overview / Pipeline / Lead Pool / Lead Status) take a
  // `variant` prop selecting which of their existing blocks to render.
  const bandProps: BandProps = {
    report,
    totals,
    scopedRow,
    loading,
    agentEntries,
    canSeeTrustability,
    embedded,
    from,
    to,
    periodLabel,
    ownerId,
    ownerOptions,
    navigate,
    dash,
    agentCell,
    expandAllAction,
    drillCell,
    poolTotal,
    poolMonth,
    poolCarry,
    poolUnassigned,
    unassignedOpenCurrent,
    unassignedOpenCarry,
    activeSum,
    largeTotal,
    largeMonth,
    largeCarry,
    stTotal,
    stMonth,
    stCarry,
    sbTotal,
    sbMonth,
    sbCarryCur,
    sbMtd,
    sbCarryCum,
    openTotalPool,
    openCurrentPool,
    openCarryPool,
    openTotalStage,
    openCurrentStage,
    openCarryStage,
    openTotalSub,
    openCurrentSub,
    openCarrySub,
    openTotalLarge,
    openCurrentLarge,
    openCarryLarge,
    openTotalTerminal,
    scopedWon,
    scopedActive,
    scopedLost,
    scopedLostCurrent,
    scopedAtv,
    perfWon,
    lossDecided,
    lstats,
    openActiveUnpublished,
    todaysPipelineData: todaysPipeline.data,
    todaysPipelineLoading: todaysPipeline.isLoading,
    expectedPipelineData: expectedPipeline.data,
    expectedPipelineLoading: expectedPipeline.isLoading,
    todayPipeRows,
    mtdPipeRows,
    realisedCount,
    sumPipeTtv,
    realisedRows,
    realisedTotalRow,
    perfDay,
    istToday,
    isPerfToday,
    setPerfDay,
    perfDayData: perfDayQuery.data,
    perfDayLoading: perfDayQuery.isLoading,
    lostReasonPivotData: lostReasonPivot.data,
    lostReasonPivotLoading: lostReasonPivot.isLoading,
    lostFilter,
    setLostFilter,
    destinationPivotData: destinationPivot.data,
    destinationPivotLoading: destinationPivot.isLoading,
  };

  return (
    <div className="space-y-4">
      {/* Page header + own date filter only when standalone. Embedded under the
          Leads inbox, the inbox supplies the title context and DateRangeFilter. */}
      {!embedded && (
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-foreground text-lg font-semibold">Leads Tracker</h1>
            <p className="text-muted-foreground text-sm">
              {isCurrentMonth ? 'Month to date' : 'Full month'} - {from} to {to}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Global owner filter - scopes every tab's KPIs (not the tables,
                which keep the full per-agent breakdown). Role-scoped like the
                Lead Inbox: admin -> everyone, sales-lead -> their team. */}
            {ownerOptions.length > 1 && (
              <UzSelect
                value={ownerId ?? ''}
                onValueChange={(v) => setOwnerId(v || null)}
                triggerClassName="h-9 w-44"
                options={[
                  { value: '', label: 'All owners' },
                  ...ownerOptions.map((owner) => ({ value: owner.id, label: owner.name })),
                ]}
              />
            )}
            <MonthYearFilter month={selMonth} year={selYear} onChange={setMonthYear} />
          </div>
        </header>
      )}

      <div>
        {/* Report-section toolbar: refresh + export now live inside the report
            (above the tabs), not the page-top header - same placement whether
            standalone or embedded. Exports gate on the shared export predicate.
            The month/year picker is NOT rendered here when embedded: the Lead
            Inbox header owns a single month picker that writes the same
            `?month&year` params this report reads. Standalone keeps its own
            picker in the page header (below, under `!embedded`). */}
        {/* Report-section toolbar renders only standalone. Embedded under the
            Lead Inbox there is nothing in this row: the header owns the single
            "Refresh everything" button, Export is removed for now, and the
            "Updated ago" stamp is dropped. */}
        {!embedded && (
          <div className="mb-2 flex flex-wrap items-center justify-end gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {updatedAgo && (
                <span className="text-muted-foreground text-xs whitespace-nowrap">
                  Updated {updatedAgo}
                </span>
              )}
              <UzButton
                variant="outline"
                size="sm"
                onClick={onRefresh}
                disabled={refreshing}
                title="Refresh report data"
              >
                <RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />
              </UzButton>
              {canExport && (
                <UzButton
                  variant="outline"
                  size="sm"
                  onClick={() => void onExport()}
                  disabled={exporting || loading}
                >
                  <Download className="mr-1.5 h-4 w-4" />
                  {exporting ? 'Exporting…' : 'Export XLSX'}
                </UzButton>
              )}
            </div>
          </div>
        )}
        {/* Stacked report sections (no tabs), re-sequenced into the client's
            dashboard flow: Target, Pipeline, Daily Tasks, Lead Pool, Active
            Lead Breakup, Lead Status, Performance (incl. Business Number),
            Pipeline Detail, Lost Leads, Bookings. Bands with their own internal
            SectionLabel (Lead Pool, Performance) render bare; the rest get the
            shared titled-section chrome below. Pure relocation of rendered
            blocks - no data/metric change. */}
        <div className="flex flex-col gap-8">
          {/* 1. Target strip (standalone only - embedded gets it from the inbox). */}
          {!embedded && <OverviewBand {...bandProps} variant="target" />}

          {/* 2. Pipeline summary (KPI strip incl. Today's Sale + Today's Pipeline). */}
          <section className="flex flex-col gap-3">
            <h2 className="flex items-center gap-2 border-b border-(--ds-border) pb-1.5 text-base font-semibold text-[#094053]">
              <span className="text-[#094053]">
                <TrendingUp className="h-4 w-4" />
              </span>
              Pipeline
            </h2>
            <PipelineBand {...bandProps} variant="summary" />
          </section>

          {/* 3. Daily Tracker = the operational strip (single source of truth,
              clickable) placed BELOW Today's Pipeline, ABOVE the Agent by Daily
              Tasks table. Shown in both modes; the Lead Inbox no longer renders
              its own top strip (it lives here now), so there is no duplicate. */}
          <section className="flex flex-col gap-3">
            <h2 className="flex items-center gap-2 border-b border-(--ds-border) pb-1.5 text-base font-semibold text-[#094053]">
              <span className="text-[#094053]">
                <ClipboardList className="h-4 w-4" />
              </span>
              Daily Tracker
            </h2>
            <LeadsTrackerStrip todayOnly ownerId={ownerId} />
            <ReportSection title="Agent by Daily Tasks (Today only)" actions={expandAllAction}>
              <LegacyTable
                loading={loading || !report}
                headers={[
                  'Agent',
                  "Today's Fresh",
                  "Today's Follow Up (Curr / CF)",
                  'Missed Follow Up (Curr / CF)',
                  'Revert Pending (Curr / CF)',
                  'Total Tasks (Curr / CF)',
                ]}
                rows={agentEntries.map(({ row, ...entry }) => [
                  { node: agentCell({ row, ...entry }), sort: row.agentName },
                  ...dailyTasksRow(row),
                ])}
                totals={totals && report ? ['Total', ...dailyTasksRow(totals)] : undefined}
              />
            </ReportSection>
          </section>

          {/* 4. Lead Pool cards + Agent-by-Lead-Pool table (own SectionLabel). */}
          <LeadPoolBand {...bandProps} variant="pool" />

          {/* 5. Active Lead Breakup: Active breakup card + funnel + Large Lead
              Destination. */}
          <section className="flex flex-col gap-3">
            <h2 className="flex items-center gap-2 border-b border-(--ds-border) pb-1.5 text-base font-semibold text-[#094053]">
              <span className="text-[#094053]">
                <Layers className="h-4 w-4" />
              </span>
              Active Lead Breakup
            </h2>
            <LeadPoolBand {...bandProps} variant="active" />
            <LeadStatusBand {...bandProps} variant="largeDestination" />
          </section>

          {/* 6. Lead Status KPIs + Lead Status by Agent table. */}
          <section className="flex flex-col gap-3">
            <h2 className="flex items-center gap-2 border-b border-(--ds-border) pb-1.5 text-base font-semibold text-[#094053]">
              <span className="text-[#094053]">
                <Activity className="h-4 w-4" />
              </span>
              Lead Status
            </h2>
            <LeadStatusBand {...bandProps} variant="status" />
          </section>

          {/* 7. Performance: cards (own SectionLabel) + insights tables +
              Business Number table. */}
          <div className="flex flex-col gap-4">
            <PerformanceBand {...bandProps} />
            <PerformanceInsightsBand {...bandProps} />
            <OverviewBand {...bandProps} variant="business" />
          </div>

          {/* 8. Pipeline detail (MTD Pipeline + Realised Pipeline tables). */}
          <section className="flex flex-col gap-3">
            <h2 className="flex items-center gap-2 border-b border-(--ds-border) pb-1.5 text-base font-semibold text-[#094053]">
              <span className="text-[#094053]">
                <TrendingUp className="h-4 w-4" />
              </span>
              Pipeline Detail
            </h2>
            <PipelineBand {...bandProps} variant="detail" />
          </section>

          {/* 9. Lost Leads. */}
          <section className="flex flex-col gap-3">
            <h2 className="flex items-center gap-2 border-b border-(--ds-border) pb-1.5 text-base font-semibold text-[#094053]">
              <span className="text-[#094053]">
                <XCircle className="h-4 w-4" />
              </span>
              Lost Leads
            </h2>
            <LostLeadsBand {...bandProps} />
          </section>

          {/* 10. Bookings & Destinations. */}
          <section className="flex flex-col gap-3">
            <h2 className="flex items-center gap-2 border-b border-(--ds-border) pb-1.5 text-base font-semibold text-[#094053]">
              <span className="text-[#094053]">
                <CalendarCheck className="h-4 w-4" />
              </span>
              Bookings & Destinations
            </h2>
            <BookingsBand {...bandProps} />
          </section>
        </div>
      </div>
      <LeadDrillDialog drill={drill} onClose={() => setDrill(null)} />
    </div>
  );
};
